import { direction_between, distance2_between_points, distance_between_points, Point } from "../../utils/math";
import { GameAgent } from "../ai/GameAgent";
import { AssetManager } from "../AssetManager";
import { GameState } from "../consts/GameState";
import { Effect } from "../models/Effect";
import { Enemy } from "../models/Enemy";
import { GameMap } from "../models/GameMap";
import { GameUi } from "../ui/GameUi";
import { GameView } from "../view/GameView";

export class Game {
    public ui: GameUi;

    public score: number = 1;
    public view: GameView;
    public map: GameMap = new GameMap();
    public enemies: Array<Enemy> = [];
    public effects: Array<Effect> = [];
    public wave_counter: number = 1;
    public money: number = 60;
    public tower_cost_multiplier: number = 1;

    public spawning: Array<Enemy> = [];
    public spawn_cooldown = 0;
    public wave_cooldown = 0;

    public lives: number = 50;

    public state: GameState;
    public agent: GameAgent;

    constructor(
        canvas: HTMLCanvasElement,
        asset_manager: AssetManager,
    ) {
        this.view = new GameView(canvas, asset_manager, this);
        this.ui = new GameUi(this, canvas);
        this.agent = new GameAgent(this, this.ui);
        this.state = GameState.PLAYING;
    }

    public create_enemy(): Enemy {
        const enemy = new Enemy(
            this.wave_counter * 2 * (0.9 + Math.random() / 5) + 2,
            this.wave_counter * 0.25 * (0.9 + Math.random() / 5) + 1,
        );
        return enemy;
    }

    public update(delta_seconds: number) {
        if (this.state !== GameState.PLAYING) return;
        this.spawn_cooldown -= delta_seconds;
        if (this.spawn_cooldown < 0) {
            this.spawn_enemy_from_stack();
        }
        this.update_enemies(delta_seconds);
        this.update_towers(delta_seconds);
        this.update_effects(delta_seconds);
    }

    private update_towers(delta_seconds: number) {
        this.map.fields.forEach((field) => {
            if (!field.tower) return;
            field.tower.cooldown -= delta_seconds;
            if (field.tower.cooldown > 0) return;
            const tower = field.tower;
            const enemies_in_range = this.enemies.filter((enemy) => {
                const enemy_position = this.map.get_position_of_path(enemy.progress);
                const dist2 = distance2_between_points(enemy_position, field.position);
                return dist2 < tower.range_squared;
            });
            const enemy = enemies_in_range.shift();
            if (!enemy) return;
            tower.cooldown = 1 / tower.shots_per_seconds;

            const start_position: Point = [...field.position];
            const target_position: Point = this.map.get_position_of_path(enemy.progress);
            this.effects.push(new Effect(
                start_position,
                target_position,
                direction_between(start_position, target_position),
                tower.projectile_speed,
            ));
        });
    }

    private update_effects(delta_seconds: number) {
        this.effects = this.effects.filter((effect: Effect) => {
            const dist_to_goal2 = distance2_between_points(effect.position, effect.target);
            effect.position[0] += effect.direction[0] * delta_seconds * effect.speed;
            effect.position[1] += effect.direction[1] * delta_seconds * effect.speed;
            const dist_to_goal2_after = distance2_between_points(effect.position, effect.target);
            effect.done = (dist_to_goal2_after > dist_to_goal2);
            if (effect.done) this.deal_damage(effect);
            return !effect.done;
        });
    }

    private deal_damage(effect: Effect) {
        this.enemies.forEach((enemy) => {
            const enemy_position = this.map.get_position_of_path(enemy.progress);
            const dist2 = distance2_between_points(enemy_position, effect.position);
            if (dist2 > effect.area_of_effect * effect.area_of_effect) return;
            enemy.health -= effect.damage;
        });
    }

    private update_enemies(delta_seconds: number) {
        if (this.enemies.length === 0) {
            this.wave_cooldown -= delta_seconds;
            if (this.wave_cooldown < 0) {
                const count = Math.floor(Math.random() * this.wave_counter + 1);
                const health = this.wave_counter * 2 * (0.9 + Math.random() / 5) + 2;
                const speed = this.wave_counter * 0.25 * (0.9 + Math.random() / 5) + 1;
                this.spawning = ([...new Array(count)]).map(() => {
                    return new Enemy(health, speed);
                });
                this.wave_counter++;
                this.money += this.wave_counter * 5;
            }
            return;
        }
        this.enemies.forEach((enemy) => {
            enemy.progress += delta_seconds * enemy.speed;
        });
        this.enemies = this.enemies.filter((enemy: Enemy) => {
            if (enemy.health <= 0) {
                this.score += 2;
                this.money += 5;
                return false;
            }
            if (enemy.progress >= this.map.path.length - 1) {
                this.lives--;
                return false;
            };
            return true;
        });
        if (this.lives <= 0) {
            this.state = GameState.LOST;
        }
    }

    private spawn_enemy_from_stack() {
        const enemy = this.spawning.pop();
        if (!enemy) return;
        this.spawn_cooldown = 1 / enemy.speed;
        this.enemies.push(enemy);
    }

    public get_score(): number {
        return this.score;
    }
}